import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { QrCode, Package } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Helper to format BOM quantity display (no conversion needed - everything is in native units)
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

const LogPickModal = ({ isOpen, onClose, workOrder, component, onSuccess }) => {
  const [lotNumber, setLotNumber] = useState("");
  const [quantity, setQuantity] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentPicks, setRecentPicks] = useState([]);

  useEffect(() => {
    if (isOpen && component) {
      // Get recent picks from all lots
      const picks = [];
      component.lots?.forEach(lot => {
        lot.picks?.forEach(pick => {
          picks.push({
            ...pick,
            lot_number: lot.lot_number
          });
        });
      });
      // Sort by picked_at descending (most recent first)
      picks.sort((a, b) => new Date(b.picked_at) - new Date(a.picked_at));
      setRecentPicks(picks.slice(0, 5)); // Show last 5 picks
    }
  }, [isOpen, component]);

  const handleScanQR = () => {
    // Placeholder for QR code scanning
    toast.info("QR Code scanning will be implemented with WMS/NetSuite integration");
    // For now, you could set a dummy pallet ID
    setLotNumber("PALLET-" + Math.random().toString(36).substring(2, 10).toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lotNumber || !quantity || parseFloat(quantity) <= 0) {
      toast.error("Please enter valid lot number and quantity");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/production/${workOrder.wo_id}/log-pick`, null, {
        params: {
          component_id: component.component_id,
          lot_number: lotNumber,
          quantity: parseFloat(quantity),
          picked_by: "warehouse"
        }
      });
      
      toast.success("Pick logged successfully");
      setLotNumber("");
      setQuantity("");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error logging pick:", error);
      toast.error(error.response?.data?.detail || "Failed to log pick");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Log Pick - {component?.component_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Lot Number Input with QR Scanner */}
          <div>
            <Label htmlFor="lot-number">Lot Number / Pallet ID</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="lot-number"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                placeholder="Enter lot number or scan QR code"
                required
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleScanQR}
                className="flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                Scan QR
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              QR code will link to WMS/NetSuite pallet data
            </p>
          </div>

          {/* Quantity Input */}
          <div>
            <Label htmlFor="quantity">Quantity ({component?.unit})</Label>
            <Input
              id="quantity"
              type="number"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity picked"
              required
            />
          </div>

          {/* Recent Pick History */}
          {recentPicks.length > 0 && (
            <div>
              <Label className="mb-2 block">Recent Picks</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {recentPicks.map((pick, idx) => (
                  <Card key={idx} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start text-sm">
                        <div>
                          <div className="font-mono font-semibold">{pick.lot_number}</div>
                          <div className="text-xs text-gray-600">
                            {formatBOMQuantity(pick.picked_qty, component?.unit, component?.item_type)} • {pick.picked_by}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(pick.picked_at).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Logging..." : "Log Pick"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogPickModal;
